import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import {
  addDays,
  filtersToQuery,
  type FilterOptions,
  type Filters,
  type ReportSummary,
} from '../lib/reporting';

interface ReportCtx {
  options: FilterOptions | null;
  filters: Filters;
  setFilters: (next: Partial<Filters>) => void;
  summary: ReportSummary | null;
  /** Same filters over the immediately-preceding equal-length period (for deltas). */
  prevSummary: ReportSummary | null;
  loading: boolean;
  error: string | null;
}

const Ctx = createContext<ReportCtx | null>(null);

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Read the initial filter state from the URL query so links are shareable. */
function filtersFromParams(params: URLSearchParams): Filters {
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  return {
    from: ISO_DATE.test(from) ? from : '',
    to: ISO_DATE.test(to) ? to : '',
    region: params.get('region') ?? '',
    store: params.get('store') ?? '',
    driver: params.get('driver') ?? '',
  };
}

/** Days between two ISO dates, inclusive of both ends. */
function inclusiveDays(from: string, to: string): number {
  const a = new Date(`${from}T00:00:00Z`).getTime();
  const b = new Date(`${to}T00:00:00Z`).getTime();
  return Math.round((b - a) / 86_400_000) + 1;
}

export function ReportProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [options, setOptions] = useState<FilterOptions | null>(null);
  // Initialise from the URL (shareable links). Read once — filters state owns it after that.
  const [filters, setFiltersState] = useState<Filters>(() => filtersFromParams(searchParams));
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [prevSummary, setPrevSummary] = useState<ReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load filter options + data bounds once. If the URL didn't already pin a
  // range, default to the last 30 days of available data (the source isn't
  // "today"-current, so a literal Yesterday would be empty).
  useEffect(() => {
    let active = true;
    apiFetch<FilterOptions>('/api/reports/filters')
      .then((opts) => {
        if (!active) return;
        setOptions(opts);
        if (opts.dataMax) {
          setFiltersState((prev) =>
            prev.from || prev.to
              ? prev
              : { ...prev, to: opts.dataMax!, from: addDays(opts.dataMax!, -29) },
          );
        }
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof ApiError ? err.message : 'Failed to load filters');
      });
    return () => {
      active = false;
    };
  }, []);

  const setFilters = useCallback((next: Partial<Filters>) => {
    setFiltersState((prev) => ({ ...prev, ...next }));
  }, []);

  // Mirror the resolved filters into the URL query string (replace, so the
  // back button isn't spammed) once a range is set — makes the view linkable.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;
  useEffect(() => {
    if (!filters.from || !filters.to) return;
    const q = filtersToQuery(filters);
    setSearchParamsRef.current(new URLSearchParams(q), { replace: true });
  }, [filters]);

  // Refetch the current + previous-period summaries whenever filters change.
  useEffect(() => {
    if (!filters.from || !filters.to) return;
    let active = true;
    setLoading(true);
    setError(null);

    const current = apiFetch<ReportSummary>(`/api/reports/summary?${filtersToQuery(filters)}`);

    // Previous period: same length, immediately before `from`, same dimensions.
    const span = inclusiveDays(filters.from, filters.to);
    const prevTo = addDays(filters.from, -1);
    const prevFrom = addDays(prevTo, -(span - 1));
    const prevFilters: Filters = { ...filters, from: prevFrom, to: prevTo };
    const previous = apiFetch<ReportSummary>(`/api/reports/summary?${filtersToQuery(prevFilters)}`).catch(
      () => null,
    );

    current
      .then((s) => {
        if (active) setSummary(s);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof ApiError ? err.message : 'Failed to load report');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    previous.then((s) => {
      if (active) setPrevSummary(s);
    });

    return () => {
      active = false;
    };
  }, [filters]);

  const value = useMemo(
    () => ({ options, filters, setFilters, summary, prevSummary, loading, error }),
    [options, filters, setFilters, summary, prevSummary, loading, error],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useReport(): ReportCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useReport must be used within ReportProvider');
  return ctx;
}
